import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ImageManager from './ImageManager';

interface FormField {
    id: string;
    type: string;
    label: string;
    name: string;
    options?: string;
}

interface Variant {
    id: string;
    name: string;
    targetUrl: string;
    weight: number;
    isPublic: boolean;
    isDeleted: boolean;
    visitors: number;
    leads: number;
    createdAt: string;
    title?: string;
    subtitle?: string;
    bgColor?: string;
    imageUrl?: string;
    formQuestions?: string;
}

interface Props {
    landingPageId: string;
    apiBase: string;
    onUpdate: () => void;
}

const DEFAULT_FORM_FIELDS: FormField[] = [
    { id: '1', type: 'tel', label: 'Mobile Number', name: 'mobile' },
    { id: '2', type: 'select', label: 'NEET Target Year', name: 'year', options: 'NEET 2025, NEET 2026, NEET 2027' },
    { id: '3', type: 'checkbox', label: 'I consent to receiving communications', name: 'consent' }
];

// Generate complete HTML for variant (Pixel-Perfect Dynamic Neetprep Template)
const generateVariantHTML = (formData: any): string => {
    const title = formData.title || 'TARGET BATCH';
    const subtitle = formData.subtitle || "India's #1 course for MCQ";
    const bgColor = formData.bgColor || '#ffffff';
    const imageUrl = formData.imageUrl || '';
    const formQuestionsRaw = formData.formQuestions || '[]';

    let formFieldsHTML = '';
    try {
        const questions = JSON.parse(formQuestionsRaw);
        if (Array.isArray(questions)) {
            formFieldsHTML = questions.map((q: any) => {
                if (q.type === 'tel') {
                    return `<div class="input-row"><div class="prefix">IN +91</div><input type="tel" placeholder="${q.label || 'Mobile Number'}" disabled></div>`;
                } else if (q.type === 'select') {
                    const options = (q.options || '').split(',').map((o: string) => `<option>${o.trim()}</option>`).join('');
                    return `<div class="input-row"><select disabled>${options}</select></div>`;
                } else if (q.type === 'checkbox') {
                    return `<div class="checkbox-container"><input type="checkbox" checked disabled><label>${q.label}</label></div>`;
                }
                return `<div class="input-row"><input type="${q.type || 'text'}" placeholder="${q.label || ''}" disabled></div>`;
            }).join('');
        }
    } catch (e) {
        formFieldsHTML = '<p style="color:red">Invalid form config</p>';
    }

    const bodyBgStyle = bgColor && bgColor !== '#ffffff' 
        ? `background-color: ${bgColor};` 
        : `background: linear-gradient(to bottom, #ffffff 0%, #f3f4f6 100%); background-color: #ffffff;`;

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Preview</title>
    <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800&display=swap" rel="stylesheet">
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Outfit', -apple-system, sans-serif; ${bodyBgStyle} color: #111827; min-height: 100vh; padding-bottom: 2rem; }
        .header { padding: 1rem; text-align: center; background: white; border-bottom: 1px solid #eee; }
        .header img { height: 40px; }
        .main-container { max-width: 500px; margin: 1.5rem auto; padding: 0 1rem; text-align: center; }
        .title-gradient { font-size: 2.5rem; font-weight: 900; text-transform: uppercase; background: linear-gradient(90deg, #f59e0b 0%, #ef4444 50%, #ec4899 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 0.5rem; letter-spacing: -0.02em; }
        .subtitle-box { display: inline-block; background: #bae6fd; color: #0f172a; padding: 0.3rem 0.8rem; border-radius: 6px; font-size: 1.2rem; font-weight: 800; margin-bottom: 0.8rem; }
        .tagline { font-size: 1.4rem; font-weight: 800; margin-bottom: 1.5rem; color: #000; }
        .form-card { background: #fffcf0; border: 2px solid #fef3c7; border-radius: 24px; padding: 1.5rem; box-shadow: 0 10px 25px rgba(0,0,0,0.05); text-align: left; }
        .form-card h3 { color: #d97706; font-size: 1.4rem; font-weight: 800; margin-bottom: 1.2rem; text-align: center; }
        .input-row { display: flex; background: white; border: 2px solid #f3f4f6; border-radius: 10px; margin-bottom: 1rem; overflow: hidden; }
        .input-row .prefix { padding: 0.8rem; background: #f9fafb; border-right: 1px solid #f3f4f6; font-weight: 700; color: #374151; display: flex; align-items: center; font-size: 0.9rem; }
        .input-row input, .input-row select { flex: 1; padding: 0.8rem; border: none; font-size: 1rem; outline: none; width: 100%; }
        .checkbox-container { display: flex; gap: 0.6rem; margin-bottom: 1.5rem; align-items: flex-start; }
        .checkbox-container input { width: 18px; height: 18px; accent-color: #d97706; margin-top: 2px; }
        .checkbox-container label { font-size: 0.8rem; color: #4b5563; line-height: 1.4; }
        .submit-btn { width: 100%; padding: 1rem; background: #d97706; background: linear-gradient(180deg, #f59e0b 0%, #d97706 100%); color: white; border: none; border-radius: 12px; font-size: 1.2rem; font-weight: 800; text-transform: uppercase; cursor: pointer; box-shadow: 0 4px 0 #b45309; }
    </style>
</head>
<body>
    <div class="header"><img src="https://www.neetprep.com/logo.png" alt="logo"></div>
    <div class="main-container">
        <h1 class="title-gradient">${title}</h1>
        <div class="subtitle-box">${subtitle}</div>
        <div class="tagline">Practice and Testing.</div>
        ${imageUrl ? `<img src="${imageUrl}" style="width:100%; border-radius:12px; margin-bottom: 1.5rem;" />` : ''}
        <div class="form-card">
            <h3>Want details?</h3>
            ${formFieldsHTML}
            <button class="submit-btn" disabled>Get Information</button>
        </div>
    </div>
</body>
</html>`;
};

export default function VariantManager({ landingPageId, apiBase, onUpdate }: Props) {
    const [variants, setVariants] = useState<Variant[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [innerTab, setInnerTab] = useState<'variants' | 'images'>('variants');
    const [formData, setFormData] = useState({
        name: '',
        targetUrl: '',
        weight: 1,
        title: '',
        subtitle: '',
        bgColor: '#ffffff',
        imageUrl: '',
        formQuestions: JSON.stringify(DEFAULT_FORM_FIELDS),
    });
    const [editingVariantId, setEditingVariantId] = useState<string | null>(null);

    // Derived state for the visual form builder
    const fields: FormField[] = (() => {
        try { return JSON.parse(formData.formQuestions); } catch { return []; }
    })();

    const updateFields = (newFields: FormField[]) => {
        setFormData({ ...formData, formQuestions: JSON.stringify(newFields) });
    };

    const handleAddField = () => {
        const newField: FormField = { id: Date.now().toString(), type: 'text', label: 'New Question', name: `field_${Date.now()}` };
        updateFields([...fields, newField]);
    };

    const handleRemoveField = (id: string) => {
        updateFields(fields.filter(f => f.id !== id));
    };

    const handleFieldChange = (id: string, updates: Partial<FormField>) => {
        updateFields(fields.map(f => f.id === id ? { ...f, ...updates } : f));
    };

    // Calculate total weight to show percentages
    const totalWeight = variants.reduce((sum, v) => sum + (v.isDeleted ? 0 : v.weight), 0);

    useEffect(() => {
        fetchVariants();
    }, [landingPageId]);

    const fetchVariants = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${apiBase}/admin/variants/${landingPageId}`);
            setVariants(response.data || []);
        } catch (error) {
            console.error('Error fetching variants:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingVariantId) {
                await axios.patch(`${apiBase}/admin/variants/${editingVariantId}`, formData);
            } else {
                await axios.post(`${apiBase}/admin/variants`, { landingPageId, ...formData });
            }
            resetForm();
            fetchVariants();
            onUpdate();
        } catch (error: any) {
            console.error('Error saving variant:', error);
            alert(`Failed to save variant.`);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '', targetUrl: '', weight: 1, title: '', subtitle: '', bgColor: '#ffffff', imageUrl: '',
            formQuestions: JSON.stringify(DEFAULT_FORM_FIELDS)
        });
        setEditingVariantId(null);
        setShowForm(false);
    };

    const handleEditVariant = (variant: Variant) => {
        setFormData({
            name: variant.name,
            targetUrl: variant.targetUrl,
            weight: variant.weight,
            title: variant.title || '',
            subtitle: variant.subtitle || '',
            bgColor: variant.bgColor || '#ffffff',
            imageUrl: variant.imageUrl || '',
            formQuestions: variant.formQuestions || JSON.stringify(DEFAULT_FORM_FIELDS)
        });
        setEditingVariantId(variant.id);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleTogglePublic = async (variantId: string, currentValue: boolean) => {
        try {
            await axios.patch(`${apiBase}/admin/variants/${variantId}`, { isPublic: !currentValue });
            fetchVariants();
        } catch (error) { console.error(error); }
    };

    const handleDeleteVariant = async (variantId: string) => {
        if (!confirm('Are you sure?')) return;
        try {
            await axios.delete(`${apiBase}/admin/variants/${variantId}`);
            fetchVariants();
        } catch (error) { console.error(error); }
    };

    if (loading) return <div className="loading">Loading variants...</div>;

    const publicUrl = `${apiBase.replace('/admin', '')}`;

    return (
        <div className="variant-manager">
            {/* Inner tabs: Variants | Images */}
            <div className="inner-tabs">
                <button
                    className={`inner-tab ${innerTab === 'variants' ? 'active' : ''}`}
                    onClick={() => setInnerTab('variants')}
                >🎯 Variants</button>
                <button
                    className={`inner-tab ${innerTab === 'images' ? 'active' : ''}`}
                    onClick={() => setInnerTab('images')}
                >📸 Images</button>
            </div>

            {innerTab === 'images' && (
                <ImageManager landingPageId={landingPageId} apiBase={apiBase} />
            )}

            {innerTab === 'variants' && (<>
            <div className="section-header">
                <div>
                    <h2>Manage Variants</h2>
                    <p className="subtitle">All variants below are served at: <code>{`https://neetprep.in/...`}</code></p>
                </div>
                <button className="btn btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
                    {showForm ? '✕ Cancel' : '+ New Variant'}
                </button>
            </div>

            {showForm && (
                <div className="variant-builder-container">
                    <div className="builder-left">
                        <form className="form variant-form" onSubmit={handleFormSubmit}>
                            <div className="form-divider"><h4>ℹ️ Basic Info</h4></div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Variant Name</label>
                                    <input type="text" placeholder="e.g., Control" value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                                </div>
                                <div className="form-group">
                                    <label>Weight (%)</label>
                                    <input type="number" min="1" max="100" value={formData.weight}
                                        onChange={(e) => setFormData({ ...formData, weight: parseInt(e.target.value) || 0 })} />
                                </div>
                            </div>

                            <div className="form-divider"><h4>🎨 Page Content</h4></div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Title</label>
                                    <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Subtitle</label>
                                    <input type="text" value={formData.subtitle} onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })} />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>BG Color</label>
                                    <input type="color" value={formData.bgColor} onChange={(e) => setFormData({ ...formData, bgColor: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Hero Image URL</label>
                                    <input type="url" value={formData.imageUrl} onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })} />
                                </div>
                            </div>

                            <div className="form-divider"><h4>📝 Form Questions</h4></div>
                            <div className="form-builder-list">
                                {fields.map((field) => (
                                    <div key={field.id} className="field-item">
                                        <div className="field-row">
                                            <select value={field.type} onChange={(e) => handleFieldChange(field.id, { type: e.target.value })}>
                                                <option value="text">Text</option>
                                                <option value="tel">Phone (IN +91)</option>
                                                <option value="select">Dropdown</option>
                                                <option value="checkbox">Checkbox</option>
                                            </select>
                                            <input type="text" placeholder="Question Label" value={field.label}
                                                onChange={(e) => handleFieldChange(field.id, { label: e.target.value })} />
                                            <button type="button" className="btn-icon danger" onClick={() => handleRemoveField(field.id)}>🗑</button>
                                        </div>
                                        {field.type === 'select' && (
                                            <input type="text" placeholder="Options (comma separated)" className="field-options"
                                                value={field.options} onChange={(e) => handleFieldChange(field.id, { options: e.target.value })} />
                                        )}
                                    </div>
                                ))}
                                <button type="button" className="btn btn-secondary btn-small" onClick={handleAddField}>
                                    ➕ Add Another Question
                                </button>
                            </div>

                            <button type="submit" className="btn btn-primary btn-large" style={{ marginTop: '20px', width: '100%' }}>
                                {editingVariantId ? '✓ Save Changes' : '✓ Create This Variant'}
                            </button>
                        </form>
                    </div>

                    <div className="builder-right">
                        <div className="preview-header">
                            <h3>📱 Live Preview</h3>
                        </div>
                        <div className="preview-frame">
                            <iframe srcDoc={generateVariantHTML(formData)} title="Variant Preview" className="preview-iframe" />
                        </div>
                    </div>
                </div>
            )}

            <div className="variants-table">
                <table>
                    <thead>
                        <tr><th>Name</th><th>Visitors</th><th>Leads</th><th>Weight</th><th>Status</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                        {variants.filter(v => !v.isDeleted).map(variant => (
                            <tr key={variant.id}>
                                <td><strong>{variant.name}</strong></td>
                                <td className="metric">{variant.visitors}</td>
                                <td className="metric">{variant.leads}</td>
                                <td className="metric">{totalWeight > 0 ? ((variant.weight / totalWeight) * 100).toFixed(1) : 0}%</td>
                                <td><span className={`status ${variant.isPublic ? 'live' : 'paused'}`}>{variant.isPublic ? '🟢 Active' : '⏸ Paused'}</span></td>
                                <td className="actions">
                                    <button className="btn btn-small" onClick={() => handleEditVariant(variant)}>✏️ Edit</button>
                                    <button className="btn btn-small" onClick={() => handleTogglePublic(variant.id, variant.isPublic)}>{variant.isPublic ? '⏸ Pause' : '▶ Resume'}</button>
                                    <button className="btn btn-small btn-danger" onClick={() => handleDeleteVariant(variant.id)}>🗑 Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            </>)}
        </div>
    );
}
